import { useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import { connect } from "react-redux";

import { PixelPlaceholderLoader } from "@similarweb/ui-components";

import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { NoData } from "pro-features/components/NoData/src/NoData";
import { StyledBox, StyledHeader, StyledTable } from "app/components/React/EngagementOverviewTable/StyledComponents";
import GAVerifiedContainer from "app/components/React/GAVerifiedIcon/GAVerifiedContainer";
import { DomainCell } from "app/components/React/Table/cells/DomainCell";
import { FullRowCell } from "app/components/React/Table/cells/FullRowCell";
import { MetricIconCell } from "app/components/React/Table/cells/MetricIconCell";
import { DefaultCellHeader } from "app/components/React/Table/headerCells/DefaultCellHeader";
import type { IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import {
    LoadingContainer,
    RowLoadingContainer,
} from "pro-features/components/Workspace/MarketingMixTable/src/MarketingMixTableStyled";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { BetaBranchLabel } from "app/pages/website-analysis/TrafficAndEngagement/BetaBranch/CommonComponents";
import { addBetaBranchParam } from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import { websiteWorldWideOverviewLogic } from "app/pages/website-performance/helpers/websitePerformanceHelpers";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { fetchSiteInfoQuery } from "app/services/sitesService/queries/sitesQueries";
import { getSiteDataFromResponse, isResultResponse } from "app/services/sitesService/sitesUtils";
import type { ISiteInfoItem } from "app/services/sitesService/types";
import { StyledPrimaryTitle } from "pro-features/styled components/StyledBoxTitle/src/StyledBoxTitle";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { granularities } from "pro-features/utils";

const fetchService = DefaultFetchService.getInstance();

interface IDataResponse {
    [key: string]: string | IMetricDataResponse;
}

interface IMetricDataResponse {
    Value: number;
    IsLeader: boolean;
}

interface IKeysDataVerification {
    [key: string]: boolean;
}

interface IGaPrivacy {
    [key: string]: boolean;
}

interface ITransformedData {
    [key: string]: IMetricMetaData | IMetricData;
}

interface IMetricMetaData {
    value: string;
    icon: string;
    tooltip: string;
    score: number;
    isBeta?: boolean;
}

interface IMetricData extends IMetricDataResponse {
    format: string;
    unlockHook?: object;
}

interface IProps {
    showGAApprovedData: boolean;
    showAddToDashboard?: boolean;
    showBetaBranchData?: boolean;
    hideTopBorderRadius?: boolean;
    endpoint?: string;
}

enum metrics {
    BounceRate = "BounceRate",
    PagesPerVisit = "PagesPerVisit",
    AvgVisitDuration = "AvgVisitDuration",
    DedupUniqueUsers = "DedupUniqueUsers",
    AvgMonthVisits = "AvgMonthVisits",
    UniqueUsers = "UniqueUsers",
    VisitsPerUser = "VisitsPerUser",
    TotalPagesViews = "TotalPagesViews",
    NewUsers = "NewUsers",
    ReturningUsers = "ReturningUsers",
}

const conditionalMetrics = [metrics.UniqueUsers, metrics.VisitsPerUser];
const constMetrics = [
    metrics.BounceRate,
    metrics.PagesPerVisit,
    metrics.AvgVisitDuration,
    metrics.DedupUniqueUsers,
    metrics.AvgMonthVisits,
    metrics.TotalPagesViews,
    metrics.NewUsers,
    metrics.ReturningUsers,
];

const getMetricsProps = (duration: string, showBetaBranchData: boolean): any => {
    const params = proNavigator.getParams();
    const { webSource } = params;
    const isNewVsReturningSupported =
        isAvailable(swSettings.components.WebNewVsReturning) && webSource !== devicesTypes.MOBILE;
    const betaBranchNonValidMetrics = {};
    const betaBranchValidMetrics = {
        [metrics.DedupUniqueUsers]: {
            value: i18nFilter()("wa.ao.metric.dedup"),
            icon: "users",
            format: "minVisitsAbbr",
            score: 4,
            tooltip: i18nFilter()("wa.ao.metric.dedup.tooltip"),
            unlockHook: {
                modal: "DeduplicationVisits",
                slide: "DeduplicationVisits",
            },
        },
        [metrics.AvgMonthVisits]: {
            value:
                duration === "28d"
                    ? i18nFilter()("wa.ao.graph.dailyavgvisits")
                    : i18nFilter()("wa.ao.graph.monthlyavgvisits"),
            icon: "visits",
            format: "minVisitsAbbr",
            score: 1,
            tooltip: i18nFilter()("wa.ao.graph.avgvisits.tooltip"),
        },
        UniqueUsers: {
            value: duration === "28d" ? i18nFilter()("wa.ao.graph.dailymuv") : i18nFilter()("wa.ao.graph.monthlymuv"),
            icon: "monthly-unique-visitors",
            format: "minVisitsAbbr",
            score: 2,
            tooltip: i18nFilter()("wa.ao.graph.muv.tooltip"),
            unlockHook: {
                modal: "UniqueVisitors",
                slide: "UniqueVisitors",
            },
        },
        VisitsPerUser: {
            value: i18nFilter()("wa.ao.graph.vpu"),
            icon: "employees",
            format: "number:2",
            score: 3,
            tooltip: i18nFilter()("wa.ao.graph.vpu.tooltip"),
            unlockHook: {
                modal: "UniqueVisitors",
                slide: "UniqueVisitors",
            },
        },
        [metrics.BounceRate]: {
            value: i18nFilter()("wa.ao.graph.bounce"),
            icon: "bounce-rate-2",
            format: "percentagesign:2",
            score: 7,
            tooltip: i18nFilter()("wa.ao.graph.bounce.tooltip"),
        },
        [metrics.PagesPerVisit]: {
            value: i18nFilter()("wa.ao.pagespervisit"),
            icon: "pages-per-visit",
            format: "number:2",
            score: 6,
            tooltip: i18nFilter()("wa.ao.graph.pages.tooltip"),
        },
        [metrics.AvgVisitDuration]: {
            value: i18nFilter()("wa.ao.graph.avgduration"),
            icon: "avg-visit-duration",
            format: "time",
            score: 5,
            tooltip: i18nFilter()("wa.ao.graph.avgduration.tooltip"),
        },
        [metrics.TotalPagesViews]: {
            value: i18nFilter()("wa.ao.graph.pagesviews"),
            icon: "pages-per-visit",
            format: "minVisitsAbbr",
            score: 8,
            tooltip: i18nFilter()("wa.ao.graph.pagesviews.tooltip"),
        },
    };

    if (isNewVsReturningSupported) {
        betaBranchValidMetrics[metrics.NewUsers] = {
            value: translate("wa.ao.graph.new.visitors"),
            icon: "share-new",
            format: "minVisitsAbbr",
            score: 9,
            tooltip: translate("wa.ao.graph.new.visitors.tooltip"),
        };
        betaBranchValidMetrics[metrics.ReturningUsers] = {
            value: translate("wa.ao.graph.returning.visitors"),
            icon: "users",
            format: "minVisitsAbbr",
            score: 10,
            tooltip: translate("wa.ao.graph.returning.visitors.tooltip"),
        };
    }

    return showBetaBranchData ? betaBranchValidMetrics : { ...betaBranchNonValidMetrics, ...betaBranchValidMetrics };
};

const unbounceUnsupportedColumns = [
    metrics.UniqueUsers,
    metrics.DedupUniqueUsers,
    metrics.VisitsPerUser,
    metrics.BounceRate,
];

function engagementOverviewTableDataAdapter(
    data: IDataResponse[],
    insertConditionalMetrics: boolean,
    hideDedupMetric: boolean,
    canAccessDedupAudience: boolean,
    canAccessUniqueVisitors: boolean,
    duration: string,
    showBetaBranchData: boolean,
    unBounced?: boolean,
) {
    let transformedData: ITransformedData[] = [];
    const metricsProps: any = getMetricsProps(duration, showBetaBranchData);
    data.map((item: IDataResponse) => {
        for (const [key, value] of Object.entries(item)) {
            if (
                (key === metrics.DedupUniqueUsers && hideDedupMetric) ||
                (key === metrics.DedupUniqueUsers && duration === "28d" && !canAccessDedupAudience) ||
                metricsProps[key] === undefined ||
                (unBounced && unbounceUnsupportedColumns.includes(key as metrics))
            ) {
                continue;
            }
            const transformedItem: ITransformedData = {};
            const itemIndex = transformedData.findIndex(
                (transformedItem: ITransformedData) =>
                    (transformedItem.metric as IMetricMetaData).value === metricsProps[key]?.value,
            );
            if (itemIndex === -1) {
                if (
                    constMetrics.includes(key as metrics) ||
                    (insertConditionalMetrics && conditionalMetrics.includes(key as metrics))
                ) {
                    transformedItem.metric = {
                        value: metricsProps[key].value,
                        icon: metricsProps[key].icon,
                        score: metricsProps[key].score,
                        tooltip: metricsProps[key].tooltip,
                        unlockHook: metricsProps[key].unlockHook,
                    };
                    if (key === metrics.DedupUniqueUsers) {
                        transformedItem.metric = {
                            ...transformedItem.metric,
                        };
                    }
                    transformedItem[item.Domain as string] = {
                        ...(value as IMetricDataResponse),
                        format: metricsProps[key].format,
                    };
                    transformedData.push(transformedItem);
                }
            } else {
                transformedData[itemIndex][item.Domain as string] = {
                    ...(value as IMetricDataResponse),
                    format: metricsProps[key].format,
                };
            }
        }
    });

    transformedData = transformedData.sort((a, b) => {
        return (a.metric as IMetricMetaData).score - (b.metric as IMetricMetaData).score;
    });

    if (!canAccessDedupAudience) {
        const index = transformedData.findIndex(
            (item: ITransformedData) => (item.metric as IMetricMetaData).value === metricsProps.DedupUniqueUsers.value,
        );
        transformedData = transformedData.concat(transformedData.splice(index, 1));
    }

    if (!canAccessUniqueVisitors) {
        const indexUniqueUsers = transformedData.findIndex(
            (item: ITransformedData) => (item.metric as IMetricMetaData).value === metricsProps.UniqueUsers.value,
        );
        const indexVisitsPerUser = transformedData.findIndex(
            (item: ITransformedData) => (item.metric as IMetricMetaData).value === metricsProps.VisitsPerUser.value,
        );
        transformedData = transformedData.concat(
            transformedData.splice(indexVisitsPerUser, 1),
            transformedData.splice(indexUniqueUsers, 1),
        );
    }

    return transformedData;
}

function getTableColumns(
    keys: string[],
    canAccessDedupAudience: boolean,
    canAccessUniqueVisitors: boolean,
    noDataDedup: boolean,
    showGAApprovedData: boolean,
    keysDataVerification: IKeysDataVerification,
    isPrivateGaList: IGaPrivacy,
) {
    return keys.map((column, index) => {
        if (column === "metric") {
            return {
                field: "metric",
                displayName: "Metric",
                headerComponent: DefaultCellHeader,
                visible: true,
                format: "None",
                cellComponent: MetricIconCell,
            };
        } else {
            return {
                field: column,
                headerComponent: DomainCell,
                inverted: true,
                cellComponent: FullRowCell,
                noDataDedup,
                colIndex: index,
                lockedMetric: !canAccessDedupAudience && !canAccessUniqueVisitors,
                lockedDedupMetric: !canAccessDedupAudience,
                lockedUniqueMetric: !canAccessUniqueVisitors,
                GAVerifiedIcon:
                    showGAApprovedData && keysDataVerification[column] ? (
                        <GAVerifiedContainer
                            size={"SMALL"}
                            isActive={true}
                            isPrivate={isPrivateGaList[column]}
                            tooltipAvailable={true}
                            metric={""}
                            tooltipIsActive={false}
                        />
                    ) : null,
            };
        }
    });
}

const Loader = Array.from(Array(6)).map((item, index) => (
    <RowLoadingContainer key={`RowLoadingContainer-${index}`}>
        <PixelPlaceholderLoader
            width={175}
            height={17}
        />
        <PixelPlaceholderLoader
            width={175}
            height={17}
        />
        <PixelPlaceholderLoader
            width={175}
            height={17}
        />
        <PixelPlaceholderLoader
            width={175}
            height={17}
        />
    </RowLoadingContainer>
));

export const EngagementOverviewTable = (props: IProps) => {
    const {
        showAddToDashboard = true,
        showGAApprovedData: ShouldGetVerifiedData,
        showBetaBranchData = false,
        endpoint,
    } = props;
    const { params } = useCurrentStateAndParams();
    const { country, webSource, key, isWWW, duration, unBounced } = params;
    const { apiController, isWWWithoutPermissions } = websiteWorldWideOverviewLogic(country);
    const { from, to, isWindow } = DurationService.getDurationData(isWWWithoutPermissions ? "3m" : duration).forAPI;
    const timeGran = isWindow ? granularities[0] : granularities[2];
    const uniqueVisitorsStartDate = {
        Desktop: swSettings.components.UniqueVisitors.resources.FirstAvailableSnapshot,
        MobileWeb: swSettings.components.UniqueVisitorsMobileWeb.resources.FirstAvailableSnapshot,
        Total: swSettings.components.WebAnalysis.startDate,
    };
    const fromObject = dayjs.utc(DurationService.getDurationData(duration).forAPI.from, "YYYY|MM|DD");
    const insertConditionalMetrics = !(
        fromObject.isBefore(dayjs.utc(uniqueVisitorsStartDate[webSource])) && duration === "28d"
    );
    const hideDedupMetric = webSource !== "Total" || ShouldGetVerifiedData;
    const noDataDedup = duration === "28d" && webSource === "Total";
    const canAccessDedupAudience = !swSettings.components.WebDedup.resources.IsDisabled;
    const canAccessUniqueVisitors = !swSettings.components.UniqueVisitors.IsDisabled;

    const [data, setData] = useState<ITransformedData[]>();
    const [error, setError] = useState<boolean>(false);
    const [columns, setColumns] = useState<any>();
    const addToDashboardWebSource = webSource as IWidgetModelTypesWebSource;

    function getGaPrivacy(siteInfoKey: string) {
        return fetchSiteInfoQuery({ keys: siteInfoKey }).then((res) => {
            const siteDataRecord: Record<string, ISiteInfoItem> = res ? (isResultResponse(res) ? res.result : res) : {};
            return Object.entries(siteDataRecord).reduce((retVal, [domain]) => {
                const siteData = getSiteDataFromResponse(res, domain);
                return { ...retVal, [domain]: siteData?.privacyStatus === "Private" };
            }, {});
        });
    }

    useEffect(() => {
        async function fetchData() {
            setData(null);
            try {
                const response = await fetchService.get<{
                    Data: IDataResponse[];
                    KeysDataVerification: IKeysDataVerification;
                }>(
                    `/${endpoint ?? `widgetApi/${apiController}/EngagementOverview/Table`}`,
                    addBetaBranchParam(
                        {
                            ShouldGetVerifiedData,
                            country,
                            from,
                            includeLeaders: true,
                            includeSubDomains: isWWW === "*",
                            isWindow,
                            keys: key.split(","),
                            timeGranularity: timeGran,
                            to,
                            webSource,
                            unBounced: String(unBounced).toLowerCase() === "true" && !ShouldGetVerifiedData, // unBounced is not supported for GA Approved data
                        },
                        showBetaBranchData,
                        false,
                    ),
                );
                const transformedData = engagementOverviewTableDataAdapter(
                    response.Data,
                    insertConditionalMetrics,
                    hideDedupMetric,
                    canAccessDedupAudience,
                    canAccessUniqueVisitors,
                    duration,
                    showBetaBranchData,
                    unBounced,
                );

                const isPrivateGaList = await getGaPrivacy(key as string);

                if (transformedData.length > 0) {
                    setColumns(
                        getTableColumns(
                            Object.keys(transformedData[0]),
                            canAccessDedupAudience,
                            canAccessUniqueVisitors,
                            noDataDedup,
                            ShouldGetVerifiedData,
                            response.KeysDataVerification,
                            isPrivateGaList,
                        ),
                    );
                }
                setError(false);
                setData(transformedData);
            } catch (e) {
                setError(true);
            }
        }

        fetchData();
    }, [
        from,
        to,
        isWindow,
        timeGran,
        country,
        webSource,
        isWWW,
        key,
        unBounced,
        ShouldGetVerifiedData,
        duration,
        showBetaBranchData,
        insertConditionalMetrics,
        hideDedupMetric,
        canAccessDedupAudience,
        canAccessUniqueVisitors,
        noDataDedup,
    ]);

    function getComponent() {
        if (data && columns) {
            return (
                <StyledTable
                    colsCount={columns.length}
                    className="MiniFlexTable"
                    data={data}
                    columns={columns}
                />
            );
        } else if (error || (data && data.length === 0)) {
            return (
                <div style={{ padding: "50px" }}>
                    <NoData title={"apps.engagementoverview.nodata"} />
                </div>
            );
        } else {
            return <LoadingContainer>{Loader}</LoadingContainer>;
        }
    }

    return (
        <StyledBox hideTopBorderRadius={props.hideTopBorderRadius}>
            <StyledHeader>
                <div>
                    <StyledPrimaryTitle>
                        <BoxTitle tooltip={i18nFilter()("wa.ao.engagement.tooltip")}>
                            {i18nFilter()("wa.ao.engagement")}
                        </BoxTitle>
                    </StyledPrimaryTitle>
                </div>
                {showAddToDashboard && !showBetaBranchData ? (
                    <AddToDashboard
                        webSource={addToDashboardWebSource}
                        type="Table"
                        metric="EngagementOverview"
                        modelType="fromWebsite"
                    />
                ) : null}
                {showBetaBranchData ? (
                    <div>
                        <BetaBranchLabel />
                    </div>
                ) : null}
            </StyledHeader>
            {getComponent()}
        </StyledBox>
    );
};

const mapStateToProps = ({ common: { showGAApprovedData } }) => {
    return {
        showGAApprovedData,
    };
};

export default connect(mapStateToProps)(EngagementOverviewTable);
